import api from '../lib/api';
import {
  CustomFieldDefinition,
  CustomFieldValue,
  CreateCustomFieldDefinitionRequest,
  UpdateCustomFieldDefinitionRequest,
  CreateCustomFieldValueRequest,
  UpdateCustomFieldValueRequest,
  CustomFieldsResponse,
  CustomFieldDefinitionsResponse,
  CustomFieldValueCountResponse,
  Workspace
} from '../types/custom-fields';

// =========================================
// CUSTOM FIELD DEFINITIONS API
// =========================================

export const getCustomFieldDefinitions = async (
  organizationId: string,
  entityType?: string,
  isActive?: boolean
): Promise<CustomFieldDefinition[]> => {
  const params = new URLSearchParams({ organization_id: organizationId });
  if (entityType) params.append('entity_type', entityType);
  if (isActive !== undefined) params.append('is_active', String(isActive));
  // Add timestamp to bust cache
  params.append('_t', Date.now().toString());
  
  const response = await api.get<CustomFieldDefinitionsResponse>(
    `/custom-fields/definitions?${params.toString()}`
  );
  return response.data.definitions;
};

export const getCustomFieldDefinition = async (id: string): Promise<CustomFieldDefinition> => {
  const response = await api.get<{ success: boolean; definition: CustomFieldDefinition }>(
    `/custom-fields/definitions/${id}`
  );
  return response.data.definition;
};

export const createCustomFieldDefinition = async (
  data: CreateCustomFieldDefinitionRequest
): Promise<CustomFieldDefinition> => {
  const response = await api.post<{ success: boolean; definition: CustomFieldDefinition }>(
    '/custom-fields/definitions',
    data
  );
  return response.data.definition;
};

export const updateCustomFieldDefinition = async (
  id: string,
  data: UpdateCustomFieldDefinitionRequest
): Promise<CustomFieldDefinition> => {
  const response = await api.put<{ success: boolean; definition: CustomFieldDefinition }>(
    `/custom-fields/definitions/${id}`,
    data
  );
  return response.data.definition;
};

export const deleteCustomFieldDefinition = async (id: string): Promise<void> => {
  await api.delete(`/custom-fields/definitions/${id}`);
};

// =========================================
// CUSTOM FIELD VALUES API
// =========================================

export const getCustomFieldValues = async (
  entityType: string,
  entityId: string,
  workspaceId?: string
): Promise<{ values: CustomFieldValue[]; workspaces: Workspace[] }> => {
  const params = new URLSearchParams({
    entity_type: entityType,
    entity_id: entityId
  });
  if (workspaceId) params.append('workspace_id', workspaceId);
  // Add timestamp to bust cache
  params.append('_t', Date.now().toString());
  
  const response = await api.get<CustomFieldsResponse>(
    `/custom-fields/values?${params.toString()}`
  );
  return {
    values: response.data.values,
    workspaces: response.data.workspaces
  };
};

export const getCustomFieldValueCount = async (
  entityType: string,
  entityId: string,
  workspaceId?: string
): Promise<number> => {
  const params = new URLSearchParams({
    entity_type: entityType,
    entity_id: entityId
  });
  if (workspaceId) params.append('workspace_id', workspaceId);
  
  const response = await api.get<CustomFieldValueCountResponse>(
    `/custom-fields/values/count?${params.toString()}`
  );
  return response.data.count;
};

export const createCustomFieldValue = async (
  data: CreateCustomFieldValueRequest
): Promise<CustomFieldValue[]> => {
  const response = await api.post<{ success: boolean; values: CustomFieldValue[] }>(
    '/custom-fields/values',
    data
  );
  return response.data.values;
};

export const updateCustomFieldValue = async (
  id: string,
  data: UpdateCustomFieldValueRequest
): Promise<CustomFieldValue> => {
  const response = await api.put<{ success: boolean; value: CustomFieldValue }>(
    `/custom-fields/values/${id}`,
    data
  );
  return response.data.value;
};

export const deleteCustomFieldValue = async (id: string): Promise<void> => {
  await api.delete(`/custom-fields/values/${id}`);
};

export const getAvailableWorkspaces = async (
  entityType: string,
  entityId: string
): Promise<Workspace[]> => {
  const params = new URLSearchParams({
    entity_type: entityType,
    entity_id: entityId
  });
  
  const response = await api.get<{ success: boolean; workspaces: Workspace[] }>(
    `/custom-fields/available-workspaces?${params.toString()}`
  );
  return response.data.workspaces;
};


